import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Clock, Trash2, ScrollText, RefreshCw, Play, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useAuth } from "../context/AuthContext";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR") : "—");
const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR") : "—");

const ACTION_FILTERS = [
    { value: "", label: "Toutes les actions" },
    { value: "auth", label: "Connexions" },
    { value: "admin", label: "Actions admin" },
    { value: "gdpr", label: "RGPD" },
    { value: "retention", label: "Purges" },
    { value: "student", label: "Étudiants" },
    { value: "promotion", label: "Promotions" },
    { value: "bilan", label: "Bilans" },
];

function daysBadge(days) {
    if (days <= 7) return "bg-red-50 text-red-700 border-red-200";
    if (days <= 60) return "bg-amber-50 text-amber-800 border-amber-200";
    return "bg-ink/5 text-ink/70 border-ink/10";
}

function PurgesTable({ purges }) {
    if (!purges.length) {
        return <div className="panel p-6 text-sm text-muted2" data-testid="purges-empty">Aucune anonymisation prévue dans les 13 prochains mois.</div>;
    }
    return (
        <div className="panel overflow-x-auto" data-testid="purges-table">
            <table className="w-full text-sm">
                <thead className="bg-ink text-white text-xs">
                    <tr><th className="text-left px-3 py-2">Étudiant</th><th className="text-left px-3 py-2">Anonymisation prévue</th><th className="text-left px-3 py-2">Délai</th><th className="text-left px-3 py-2">Alertes envoyées</th></tr>
                </thead>
                <tbody>
                    {purges.map((p) => (
                        <tr key={p.student_id} className="border-t border-rule" data-testid={`purge-row-${p.student_id}`}>
                            <td className="px-3 py-2"><div className="font-medium">{p.name}</div><div className="text-xs text-muted2">{p.email}</div></td>
                            <td className="px-3 py-2 font-mono text-xs">{fmtDate(p.purge_at)}</td>
                            <td className="px-3 py-2"><span className={`pill border ${daysBadge(p.days_left)}`}>{p.days_left <= 0 ? "Échue" : `J-${p.days_left}`}</span></td>
                            <td className="px-3 py-2 text-xs font-mono">{(p.warnings_sent || []).join(", ") || "—"}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function DeletionRequests({ items }) {
    if (!items.length) return <div className="panel p-6 text-sm text-muted2" data-testid="deletions-empty">Aucune demande de suppression en cours.</div>;
    return (
        <div className="panel divide-y divide-rule" data-testid="deletions-list">
            {items.map((d) => (
                <div key={d.user_id} className="px-4 py-3 flex flex-wrap items-center justify-between gap-2 text-sm" data-testid={`deletion-row-${d.user_id}`}>
                    <div><span className="font-medium">{d.name}</span> <span className="text-xs text-muted2">· {d.role} · {d.email}</span></div>
                    <span className="pill bg-red-50 text-red-700 border border-red-200">Exécution le {fmtDate(d.deletion_scheduled_for)}</span>
                </div>
            ))}
        </div>
    );
}

function AuditLog() {
    const [action, setAction] = useState("");
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/audit-log", { params: { limit: 200, action: action || undefined } });
            setRows(data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    }, [action]);

    useEffect(() => { load(); }, [load]);

    return (
        <div>
            <div className="flex flex-wrap items-center gap-2 mb-3">
                <select value={action} onChange={(e) => setAction(e.target.value)} className="field-input !w-auto !py-1.5 text-sm" data-testid="audit-action-filter">
                    {ACTION_FILTERS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
                <button onClick={load} className="btn-outline !text-xs" data-testid="audit-refresh"><RefreshCw size={12} /> Actualiser</button>
            </div>
            <div className="panel overflow-x-auto" data-testid="audit-table">
                <table className="w-full text-xs">
                    <thead className="bg-ink text-white">
                        <tr><th className="text-left px-3 py-2">Date</th><th className="text-left px-3 py-2">Action</th><th className="text-left px-3 py-2">Auteur</th><th className="text-left px-3 py-2">Détails</th></tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td colSpan={4} className="px-3 py-4 text-muted2">Chargement…</td></tr>}
                        {!loading && !rows.length && <tr><td colSpan={4} className="px-3 py-4 text-muted2" data-testid="audit-empty">Aucune entrée.</td></tr>}
                        {!loading && rows.map((r) => (
                            <tr key={r.id} className="border-t border-rule align-top" data-testid="audit-row">
                                <td className="px-3 py-2 font-mono whitespace-nowrap">{fmtDateTime(r.ts)}</td>
                                <td className="px-3 py-2 font-mono text-orange">{r.action}</td>
                                <td className="px-3 py-2">{r.user_email || r.user_role || "—"}</td>
                                <td className="px-3 py-2 font-mono text-[11px] text-ink/70 break-all max-w-md">{r.meta && Object.keys(r.meta).length ? JSON.stringify(r.meta) : "—"}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function AdminRunButtons({ onDone }) {
    const [busy, setBusy] = useState(false);
    const run = async (dryRun) => {
        if (!dryRun && !window.confirm("Exécuter maintenant la tâche RGPD (alertes, anonymisations échues, suppressions programmées) ?")) return;
        setBusy(true);
        try {
            const { data } = await api.post("/admin/compliance/run", { dry_run: dryRun });
            toast.success(`${dryRun ? "Simulation" : "Exécution"} : ${data.warnings_queued} alerte(s), ${data.students_anonymized} anonymisation(s), ${data.deletions_executed} suppression(s).`);
            onDone();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };
    return (
        <div className="flex gap-2">
            <button onClick={() => run(true)} disabled={busy} className="btn-outline !text-xs" data-testid="compliance-dry-run">
                {busy ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />} Simuler la tâche RGPD
            </button>
            <button onClick={() => run(false)} disabled={busy} className="btn-outline !text-xs !text-destructive !border-destructive/30" data-testid="compliance-run-now">
                <Play size={12} /> Exécuter maintenant
            </button>
        </div>
    );
}

export default function Compliance() {
    const { user } = useAuth();
    const [data, setData] = useState({ purges: [], deletion_requests: [] });

    const load = useCallback(async () => {
        try {
            const { data: res } = await api.get("/retention/upcoming");
            setData(res);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        }
    }, []);

    useEffect(() => { if (["responsable", "admin"].includes(user?.role)) load(); }, [load, user?.role]);

    if (!["responsable", "admin"].includes(user?.role)) return <Navigate to="/dashboard" replace />;

    return (
        <div className="min-h-screen bg-paper flex flex-col">
            <Navbar />
            <main className="flex-1 max-w-6xl mx-auto w-full px-6 lg:px-10 py-12" data-testid="compliance-page">
                <div className="label-eyebrow flex items-center gap-2"><ShieldCheck size={14} className="text-orange" /> Conformité RGPD</div>
                <div className="flex flex-wrap items-end justify-between gap-4 mt-1">
                    <h1 className="h-display text-4xl sm:text-5xl font-semibold leading-tight">Données & traçabilité</h1>
                    {user.role === "admin" && <AdminRunButtons onDone={load} />}
                </div>
                <p className="text-sm text-muted2 mt-3 max-w-3xl">
                    Les données personnelles des étudiants sont anonymisées automatiquement 12 mois après la fin de leur formation (date de fin de promotion ou de clôture d'inscription). Les statistiques pédagogiques restent disponibles de façon anonyme.
                </p>

                <section className="mt-10">
                    <h2 className="text-base md:text-lg font-semibold flex items-center gap-2 mb-3"><Clock size={16} className="text-orange" /> Anonymisations à venir</h2>
                    <PurgesTable purges={data.purges || []} />
                </section>

                <section className="mt-10">
                    <h2 className="text-base md:text-lg font-semibold flex items-center gap-2 mb-3"><Trash2 size={16} className="text-orange" /> Demandes de suppression (délai de 30 jours)</h2>
                    <DeletionRequests items={data.deletion_requests || []} />
                </section>

                <section className="mt-10">
                    <h2 className="text-base md:text-lg font-semibold flex items-center gap-2 mb-3"><ScrollText size={16} className="text-orange" /> Journal d'audit</h2>
                    <AuditLog />
                </section>
            </main>
            <Footer />
        </div>
    );
}
